import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CalendarDays, ChevronLeft, ChevronRight, Download, CalendarClock, GraduationCap, CheckSquare } from "lucide-react";
import api from "../lib/api";
import { fmtDate } from "../lib/format";
import { downloadICS } from "../lib/integrations";
import { toast } from "sonner";
import PageHeader from "../components/PageHeader";
import { Button } from "../components/ui/button";

const TYPE_META = {
  Meeting: { color: "bg-brand", text: "text-brand", route: "/meetings", icon: CalendarClock },
  Workshop: { color: "bg-emerald-500", text: "text-emerald-600", route: "/workshops", icon: GraduationCap },
  Task: { color: "bg-amber-500", text: "text-amber-600", route: "/tasks", icon: CheckSquare },
};
const DOW = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function ymd(d) {
  const x = new Date(d);
  if (isNaN(x)) return null;
  return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, "0")}-${String(x.getDate()).padStart(2, "0")}`;
}

export default function Calendar() {
  const navigate = useNavigate();
  const [cursor, setCursor] = useState(() => { const n = new Date(); return new Date(n.getFullYear(), n.getMonth(), 1); });
  const [events, setEvents] = useState([]);

  useEffect(() => {
    Promise.all([api.get("/meetings"), api.get("/workshops"), api.get("/tasks")]).then(([m, w, t]) => {
      const ev = [];
      m.data.forEach((x) => x.date && ev.push({ type: "Meeting", title: x.clinic_name || x.meeting_type, date: x.date, location: x.location, description: x.notes }));
      w.data.forEach((x) => x.date && ev.push({ type: "Workshop", title: x.event_name, date: x.date, location: x.location, description: x.products_covered }));
      t.data.forEach((x) => x.due_date && x.status !== "Done" && ev.push({ type: "Task", title: x.title, date: x.due_date, description: x.description }));
      setEvents(ev);
    }).catch(() => toast.error("Failed to load calendar events"));
  }, []);

  const eventsByDay = useMemo(() => {
    const map = {};
    events.forEach((e) => { const k = ymd(e.date); if (k) (map[k] = map[k] || []).push(e); });
    return map;
  }, [events]);

  const grid = useMemo(() => {
    const year = cursor.getFullYear(), month = cursor.getMonth();
    const first = new Date(year, month, 1);
    const startOffset = (first.getDay() + 6) % 7; // Monday-first
    const start = new Date(year, month, 1 - startOffset);
    return Array.from({ length: 42 }, (_, i) => { const d = new Date(start); d.setDate(start.getDate() + i); return d; });
  }, [cursor]);

  const todayKey = ymd(new Date());
  const monthLabel = cursor.toLocaleDateString("en-NZ", { month: "long", year: "numeric" });
  const agenda = [...events].filter((e) => new Date(e.date) >= new Date(new Date().toDateString()))
    .sort((a, b) => new Date(a.date) - new Date(b.date)).slice(0, 12);

  return (
    <div>
      <PageHeader title="Calendar" subtitle="Meetings, workshops & tasks in one view" icon={CalendarDays}>
        <Button variant="outline" size="sm" onClick={() => downloadICS(events.map((e) => ({ title: `[${e.type}] ${e.title}`, date: e.date, location: e.location, description: e.description })))} data-testid="export-ics-btn">
          <Download className="h-4 w-4 mr-1.5" /> Export .ics
        </Button>
      </PageHeader>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white rounded-xl border border-slate-200 shadow-sm p-4">
          <div className="flex items-center justify-between mb-4 px-1">
            <h3 className="font-display font-semibold text-lg text-navy-900">{monthLabel}</h3>
            <div className="flex items-center gap-1">
              <button onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))} className="p-1.5 rounded-md hover:bg-slate-100" data-testid="cal-prev"><ChevronLeft className="h-4 w-4" /></button>
              <button onClick={() => { const n = new Date(); setCursor(new Date(n.getFullYear(), n.getMonth(), 1)); }} className="px-2.5 py-1 text-xs rounded-md hover:bg-slate-100 text-slate-500" data-testid="cal-today">Today</button>
              <button onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))} className="p-1.5 rounded-md hover:bg-slate-100" data-testid="cal-next"><ChevronRight className="h-4 w-4" /></button>
            </div>
          </div>
          <div className="grid grid-cols-7 gap-1 mb-1">
            {DOW.map((d) => <div key={d} className="text-center text-[11px] uppercase tracking-wider text-slate-400 font-semibold py-1">{d}</div>)}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {grid.map((d, i) => {
              const key = ymd(d);
              const inMonth = d.getMonth() === cursor.getMonth();
              const dayEvents = eventsByDay[key] || [];
              return (
                <div key={i} className={`min-h-[78px] rounded-lg border p-1.5 ${inMonth ? "bg-white border-slate-100" : "bg-slate-50/50 border-transparent"} ${key === todayKey ? "ring-2 ring-navy-900" : ""}`}>
                  <div className={`text-xs font-medium mb-1 ${inMonth ? "text-slate-600" : "text-slate-300"} ${key === todayKey ? "text-navy-900 font-bold" : ""}`}>{d.getDate()}</div>
                  <div className="space-y-0.5">
                    {dayEvents.slice(0, 3).map((e, j) => (
                      <button key={j} onClick={() => navigate(TYPE_META[e.type].route)} title={e.title}
                        className={`w-full text-left text-[10px] leading-tight px-1 py-0.5 rounded text-white truncate ${TYPE_META[e.type].color}`}>
                        {e.title}
                      </button>
                    ))}
                    {dayEvents.length > 3 && <div className="text-[10px] text-slate-400 px-1">+{dayEvents.length - 3} more</div>}
                  </div>
                </div>
              );
            })}
          </div>
          <div className="flex items-center gap-4 mt-4 px-1">
            {Object.entries(TYPE_META).map(([t, m]) => (
              <span key={t} className="flex items-center gap-1.5 text-xs text-slate-500"><span className={`h-2.5 w-2.5 rounded-full ${m.color}`} /> {t}</span>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
          <h3 className="font-display font-semibold text-navy-900 mb-4">Upcoming Agenda</h3>
          <div className="space-y-3">
            {agenda.length === 0 && <p className="text-sm text-slate-400">Nothing scheduled.</p>}
            {agenda.map((e, i) => {
              const m = TYPE_META[e.type]; const Icon = m.icon;
              return (
                <div key={i} onClick={() => navigate(m.route)} className="flex items-start gap-3 cursor-pointer group" data-testid={`agenda-${i}`}>
                  <div className={`h-9 w-9 rounded-lg flex items-center justify-center shrink-0 ${m.color} bg-opacity-10`}>
                    <Icon className={`h-4 w-4 ${m.text}`} />
                  </div>
                  <div className="min-w-0 flex-1 border-b border-slate-50 pb-3">
                    <div className="text-sm font-medium text-navy-900 truncate group-hover:text-brand">{e.title}</div>
                    <div className="text-xs text-slate-400">{e.type} · {fmtDate(e.date)}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
